import React from "react";
import styles from "./style.module.scss";
import { useSelector } from "react-redux";
import { RootState } from "../../../state";
import { isEqual } from "lodash";
import { getPreferredFormattedTimeSeconds } from "../../../utils/preferences";

const HEADERS = [
  { key: "energy", label: "Pit", width: 45 },
  { key: "stopDelta", label: "Δ tPit", width: 60 },
  { key: "target", label: "Trgt", width: 45 },
  { key: "lapTime", label: "LT", width: 70 },
  { key: "raceTimeDelta", label: "Δ RT", width: 60 },
  { key: "totalDelta", label: "Δ Total", width: 70 },
] as const;

export default function PitPreviewsTable({ carColour }: { carColour: string }) {
  const offsetPreviews = useSelector(
    (state: RootState) => state.raceData[carColour]?.pitPreviews ?? [],
    isEqual,
  );

  const raceConfig = useSelector(
    (state: RootState) => state.raceData[carColour]?.raceConfig,
    isEqual,
  );

  const currentLap = raceConfig?.currentLap;
  const lapsRemaining = useSelector((state: RootState) =>
    currentLap != null
      ? state.raceData[carColour]?.lapTable[currentLap]?.lapsRemaining ?? null
      : null,
  );

  const tPitBoostVsScenario = useSelector(
    (state: RootState) =>
      state.imports.imports.strategy?.data.tPitBoostVsScenario,
    isEqual,
  );

  const currentScenario = raceConfig?.pitScenario ?? null;
  const currentStopTime =
    currentScenario !== null
      ? tPitBoostVsScenario?.[currentScenario - 1] ?? null
      : null;
  const currentLapTime = raceConfig?.lapTimeRace ?? null;
  const hasPitted =
    raceConfig?.pitLap !== null && raceConfig?.pitLap !== undefined;

  return (
    <div className={styles.container}>
      <table>
        <colgroup>
          {HEADERS.map((header) => (
            <col key={header.key} style={{ width: `${header.width}px` }} />
          ))}
        </colgroup>
        <thead>
          <tr>
            {HEADERS.map((header) => (
              <th key={header.key}>{header.label}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {offsetPreviews.length > 0
            ? offsetPreviews.map((preview, index) => {
                const previewStopTime = tPitBoostVsScenario?.[index] ?? null;
                const stopDelta =
                  previewStopTime !== null && currentStopTime !== null
                    ? previewStopTime - currentStopTime
                    : null;
                const raceTimeDelta =
                  lapsRemaining !== null &&
                  currentLapTime !== null &&
                  preview.lapTime !== null
                    ? lapsRemaining * preview.lapTime -
                      lapsRemaining * currentLapTime
                    : null;
                const totalDelta = hasPitted
                  ? raceTimeDelta
                  : raceTimeDelta !== null && stopDelta !== null
                  ? raceTimeDelta + stopDelta
                  : null;

                return (
                  <tr key={index}>
                    <td>{preview.energy}</td>
                    <td
                      style={
                        stopDelta !== null && stopDelta <= -0.1
                          ? { backgroundColor: "#69db7c" }
                          : stopDelta !== null && stopDelta >= 0.1
                          ? { backgroundColor: "#ff6b6b" }
                          : undefined
                      }
                    >
                      {stopDelta !== null ? stopDelta.toFixed(1) : "-"}
                    </td>
                    <td>
                      {preview.target !== null
                        ? preview.target.toFixed(2)
                        : "-"}
                    </td>
                    <td>
                      {preview.lapTime !== null
                        ? getPreferredFormattedTimeSeconds(preview.lapTime)
                        : "-"}
                    </td>
                    <td
                      style={
                        raceTimeDelta !== null && raceTimeDelta <= -0.1
                          ? { backgroundColor: "#69db7c" }
                          : raceTimeDelta !== null && raceTimeDelta >= 0.1
                          ? { backgroundColor: "#ff6b6b" }
                          : undefined
                      }
                    >
                      {raceTimeDelta !== null ? raceTimeDelta.toFixed(1) : "-"}
                    </td>
                    <td
                      style={
                        totalDelta !== null && totalDelta <= -0.1
                          ? { backgroundColor: "#69db7c" }
                          : totalDelta !== null && totalDelta >= 0.1
                          ? { backgroundColor: "#ff6b6b" }
                          : undefined
                      }
                    >
                      {totalDelta !== null ? totalDelta.toFixed(1) : "-"}
                    </td>
                  </tr>
                );
              })
            : [-1, 0, 1].map((offset) => (
                <tr key={offset}>
                  <td>{offset}</td>
                  <td>-</td>
                  <td>-</td>
                  <td>-</td>
                  <td>-</td>
                  <td>-</td>
                </tr>
              ))}
        </tbody>
      </table>
    </div>
  );
}
